import clone from 'lodash.clonedeep';
import moment from 'moment';

import { getFilterDescription } from '../utils/ladder.util';
import { snapshotExists } from '../utils/snapshot.util';
import { toCardLabel, toImageUrl, getConditionDisplay } from '../utils/format.util';
import { getCollectionCardInvestments } from '../utils/collection.util';
import { DEFAULT_COLLECTION_DATA, DEFAULT_USER } from '../constants';

export default class AuthService {
  constructor(firebase) {
    this.firebase = firebase;
    this.userListener = null;
  }

  async loadUser(firebaseUser, newUserTheme = 'light') {
    try {
      if (!firebaseUser) {
        return {};
      }

      let user = clone(DEFAULT_USER);

      user.theme = newUserTheme;

      const userDoc = await this.firebase.firestore()
        .collection('users')
        .doc(firebaseUser.uid)
        .get();

      if (!snapshotExists(userDoc)) {
        user.uid = firebaseUser.uid;

        await this.firebase.firestore()
          .collection('users')
          .doc(firebaseUser.uid)
          .set(user);
      } else {
        user.role = userDoc.data().role || user.role;
        user.employee = userDoc.data().employee || user.employee;
        user.customerId = userDoc.data().customerId || user.customerId;
        user.subscription = userDoc.data().subscription || user.subscription;
        user.notificationSettings = userDoc.data().notificationSettings || user.notificationSettings;
        user.tutorials = userDoc.data().tutorials || user.tutorials;
        user.dashboardWidgets = userDoc.data().dashboardWidgets || user.dashboardWidgets;
        user.displayName = userDoc.data().displayName || user.displayName;
        user.photoUrl = userDoc.data().photoUrl || '';
        user.profileLink = userDoc.data().profileLink || user.profileLink;
        user.locale = userDoc.data().locale || user.locale;
        user.numCardsWatching = userDoc.data().numCardsWatching || user.numCardsWatching;
        user.numSavedFilters = userDoc.data().numSavedFilters || user.numSavedFilters;
        user.numComparisons = userDoc.data().numComparisons || user.numComparisons;
        user.hasUnreadNotifications = userDoc.data().hasUnreadNotifications || user.hasUnreadNotifications;
        user.theme = userDoc.data().theme || user.theme;
        user.recentSearches = userDoc.data().recentSearches || user.recentSearches;
      }

      user = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        provider: firebaseUser.providerData && firebaseUser.providerData.length ? firebaseUser.providerData[0].providerId : '',
        ...user,
      };

      return user;
    } catch (e) {
      return {};
    }
  }

  async loadCollections(user, activeCollectionId = null) {
    try {
      if (!user || !user.uid || !user.role) {
        return { collections: [], collection: { id: null } };
      }

      let collection = {};

      const collectionsQuery = await this.firebase.firestore()
        .collection('users')
        .doc(user.uid)
        .collection('collections')
        .get();

      const collections = [];

      collectionsQuery.forEach((collectionDoc) => {
        collections.push({
          ...DEFAULT_COLLECTION_DATA,
          ...collectionDoc.data(),
          id: collectionDoc.id,
          uid: user.uid,
        });
      });

      if (collections.length > 0) {
        [collection] = collections;

        if (Boolean(activeCollectionId) && collections.find(({ id }) => id === activeCollectionId)) {
          collection = collections.find(({ id }) => id === activeCollectionId);
        }
      } else {
        const newCollectionData = {
          ...DEFAULT_COLLECTION_DATA,
          uid: user.uid,
        };

        const newCollectionRef = await this.firebase.firestore()
          .collection('users')
          .doc(user.uid)
          .collection('collections')
          .add(newCollectionData);

        newCollectionData.id = newCollectionRef.id;
        collection = clone(newCollectionData);
        collections.push(collection);
      }

      return { collections, collection };
    } catch (e) {
      return {};
    }
  }

  async updateUser(uid, user) {
    const userToSave = clone(user);

    if (user.photoUrl && (user.photoUrl.name || user.photoUrl.size)) {
      const path = `users/${uid}/profile`;
      const imageUrl = toImageUrl(path);

      if (user.photoUrl.path) {
        await this.firebase.storage().ref(path).putFile(user.photoUrl.path);
      } else {
        await this.firebase.storage().ref().child(path).put(user.photoUrl);
      }

      userToSave.photoUrl = imageUrl;
    }

    return this.firebase.firestore().collection('users').doc(uid).update(userToSave);
  }

  async createSubscription(subscription) {
    if (subscription.id) {
      return this.firebase.firestore().collection('subscriptions').doc(subscription.id).set(subscription);
    }

    return this.firebase.firestore().collection('subscriptions').add(subscription);
  }

  async deleteAccount(uid) {
    return this.firebase.firestore().collection('userDeletes').doc(uid).set({ start: true });
  }

  async getCollection(uid, collectionId) {
    const collectionDoc = await this.firebase.firestore().collection('users').doc(uid).collection('collections')
      .doc(collectionId)
      .get();

    const collection = {
      ...collectionDoc.data(),
      id: collectionDoc.id,
    };

    return collection;
  }

  async createCollection(uid, name) {
    const newCollectionData = {
      ...DEFAULT_COLLECTION_DATA,
      uid,
      name,
    };

    const newCollectionRef = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .add(newCollectionData);

    newCollectionData.id = newCollectionRef.id;

    return newCollectionData;
  }

  async deleteCollection(uid, collectionId) {
    const collectionCardDocs = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(collectionId)
      .collection('collection_cards')
      .get();

    collectionCardDocs.forEach((doc) => {
      doc.ref.delete();
    });

    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(collectionId)
      .delete();
  }

  async updateCollectionName(uid, collectionId, name) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(collectionId)
      .update({ name });
  }

  getCollectionCardFromDoc(collectionCardDoc) {
    const collectionCard = {
      ...collectionCardDoc.data(),
      id: collectionCardDoc.id,
      willManageQuantity: typeof collectionCardDoc.data().willManageQuantity === 'boolean' ? collectionCardDoc.data().willManageQuantity : false,
      useLastSoldValue: typeof collectionCardDoc.data().useLastSoldValue === 'boolean' ? collectionCardDoc.data().useLastSoldValue : false,
      individualCards: Array.isArray(collectionCardDoc.data().individualCards) ? collectionCardDoc.data().individualCards : [],
    };

    if (collectionCard.datePurchased && typeof collectionCard.datePurchased.toDate === 'function') {
      collectionCard.datePurchased = collectionCard.datePurchased.toDate();
    }

    if (collectionCard.dateSold && typeof collectionCard.dateSold.toDate === 'function') {
      collectionCard.dateSold = collectionCard.dateSold.toDate();
    }

    if (collectionCard.playerIndexDate && typeof collectionCard.playerIndexDate.toDate === 'function') {
      collectionCard.playerIndexDate = collectionCard.playerIndexDate.toDate();
    }

    if (collectionCard.stats && collectionCard.stats.newestSale && collectionCard.stats.newestSale.date && typeof collectionCard.stats.newestSale.date === 'string') {
      collectionCard.stats.newestSale.date = new Date(collectionCard.stats.newestSale.date);
    } else if (collectionCard.stats && collectionCard.stats.newestSale && collectionCard.stats.newestSale.date && typeof collectionCard.stats.newestSale.date.toDate === 'function') {
      collectionCard.stats.newestSale.date = collectionCard.stats.newestSale.date.toDate();
    }

    collectionCard.individualCards = collectionCard.individualCards.map((item) => {
      const newItem = clone(item);

      if (item.datePurchased && typeof item.datePurchased.toDate === 'function') {
        newItem.datePurchased = item.datePurchased.toDate();
      }

      if (item.dateSold && typeof item.dateSold.toDate === 'function') {
        newItem.dateSold = item.dateSold.toDate();
      }

      return newItem;
    });

    return collectionCard;
  }

  async getCollectionCard(uid, collectionId, id) {
    const collectionCardDoc = await this.firebase
      .firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(collectionId)
      .collection('collection_cards')
      .doc(id)
      .get();

    if (!collectionCardDoc || !snapshotExists(collectionCardDoc) || !collectionCardDoc.data()) {
      return null;
    }

    return {
      ...this.getCollectionCardFromDoc(collectionCardDoc),
      id: collectionCardDoc.id,
      collectionCardId: collectionCardDoc.id,
      image: collectionCardDoc.data().imageCustom || collectionCardDoc.data().image,
      imageBack: collectionCardDoc.data().imageBack || null,
      imageCustom: collectionCardDoc.data().imageCustom || null,
      tags: collectionCardDoc.data().tags || [],
    };
  }

  async getCollectionCardByCardId(uid, collectionIds, cardId) {
    if (!cardId || !collectionIds || collectionIds.length < 1) {
      return null;
    }

    let collectionCard = {};

    for (let i = 0; i < collectionIds.length; i += 1) {
      const collectionId = collectionIds[i];
      const collectionCardQuery = await this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('collections')
        .doc(collectionId)
        .collection('collection_cards')
        .where('cardId', '==', cardId)
        .get();

      collectionCardQuery.forEach((collectionCardDoc) => {
        if (snapshotExists(collectionCardDoc) && collectionCardDoc.data()) {
          collectionCard = {
            ...this.getCollectionCardFromDoc(collectionCardDoc),
            id: collectionCardDoc.id,
            collectionCardId: collectionCardDoc.id,
          };

          i = collectionIds.length + 1;
        }
      });
    }

    delete collectionCard.stats;

    return collectionCard;
  }

  async createOrUpdateCollectionCard(uid, collectionId, card) {
    const cardToSave = clone(card);

    delete cardToSave.id;
    cardToSave.dailySync = false;

    cardToSave.image = typeof card.image === 'string' ? card.image : '';
    cardToSave.imageBack = typeof card.imageBack === 'string' ? card.imageBack : '';
    cardToSave.imageCustom = typeof card.image === 'string' ? card.image : '';

    if (!card.cardId) {
      cardToSave.label = toCardLabel(card);
    }

    if (!cardToSave.stats) {
      cardToSave.stats = {};
    }

    if (card.cardId) {
      let marketValue = card.marketValue;
      let guideValue = card.guideValue;

      if (!marketValue || !guideValue) {
        const cardDoc = await this.firebase.firestore().collection('cards').doc(card.cardId).get();

        if (snapshotExists(cardDoc) && cardDoc.data() && cardDoc.data().stats) {
          marketValue = cardDoc.data().stats.marketValue || null;
          guideValue = cardDoc.data().stats.guideValue || null;
        }
      }

      if (card.useLastSoldValue) {
        cardToSave.currentValue = marketValue || null;
        cardToSave.stats.currentValue = marketValue || null;
      } else {
        cardToSave.currentValue = guideValue || card.currentValue || null;
        cardToSave.stats.currentValue = guideValue || card.currentValue || null;
      }
    }

    if (card.willManageQuantity && card.individualCards) {
      cardToSave.individualCards = card.individualCards.filter((item) => {
        const hasInvestmentData = typeof item.investment === 'number' && typeof item.datePurchased === 'object';
        const hasSoldData = !item.sold || (typeof item.priceSold === 'number' && typeof item.dateSold === 'object');

        return hasInvestmentData && hasSoldData;
      });

      cardToSave.quantity = cardToSave.individualCards.length || 1;
    }

    const collectionCardInvestments = getCollectionCardInvestments(cardToSave);

    cardToSave.profit = collectionCardInvestments.profit;
    cardToSave.investment = collectionCardInvestments.investment;
    cardToSave.sold = collectionCardInvestments.sold;
    cardToSave.priceSold = collectionCardInvestments.priceSold;
    cardToSave.quantity = collectionCardInvestments.quantity;
    cardToSave.quantitySold = collectionCardInvestments.quantitySold;
    cardToSave.hasQuantityAvailable = collectionCardInvestments.hasQuantityAvailable;

    let { id } = card;

    if (card.id) {
      await this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('collections')
        .doc(collectionId)
        .collection('collection_cards')
        .doc(card.id)
        .update(cardToSave);
    } else {
      cardToSave.dateAdded = new Date();
      cardToSave.priceMovement = 0;

      const doc = await this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('collections')
        .doc(collectionId)
        .collection('collection_cards')
        .add(cardToSave);

      id = doc.id;
    }

    const imageUrl = await this.saveCollectionImage(uid, collectionId, id, card.image, 'image', `collections/${collectionId}/${id}`);
    const backImageUrl = await this.saveCollectionImage(uid, collectionId, id, card.imageBack, 'imageBack', `collections/${collectionId}/${id}-back`);
    const customImageUrl = await this.saveCollectionImage(uid, collectionId, id, card.image, 'imageCustom', `collections/${collectionId}/${id}`);

    return {
      ...cardToSave,
      id,
      image: imageUrl,
      imageBack: backImageUrl,
      imageCustom: customImageUrl,
    };
  }

  async saveCollectionImage(uid, collectionId, cardCollectionId, image, property, path) {
    if (image && (image.name || image.size)) {
      const imageUrl = toImageUrl(path);

      if (image.base64url) {
        await this.firebase.storage().ref().child(path).putString(image.base64url, 'data_url');
      } else if (image.path) {
        await this.firebase.storage().ref(path).putFile(image.path);
      } else {
        await this.firebase.storage().ref().child(path).put(image);
      }

      await this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('collections')
        .doc(collectionId)
        .collection('collection_cards')
        .doc(cardCollectionId)
        .update({
          [property]: imageUrl,
          dailySync: false,
        });

      return imageUrl;
    }

    return image;
  }

  async moveCollectionCard(uid, oldCollectionId, newCollectionId, collectionCardId) {
    const existingCardDoc = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(oldCollectionId)
      .collection('collection_cards')
      .doc(collectionCardId)
      .get();

    if (!snapshotExists(existingCardDoc) || !existingCardDoc.data() || !existingCardDoc.data().uid) {
      throw new Error('Collection card not found for move');
    }

    await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(newCollectionId)
      .collection('collection_cards')
      .doc(collectionCardId)
      .set({
        ...existingCardDoc.data(),
        collectionId: newCollectionId,
        dailySync: false,
      });

    await this.firebase.firestore()
      .collection('collectionMoves')
      .add({
        uid,
        oldCollectionId,
        newCollectionId,
        collectionCardId,
      });
  }

  async setCollectionPublic(uid, collectionId, isPublic) {
    const collectionDoc = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(collectionId)
      .get();

    if (snapshotExists(collectionDoc) && collectionDoc.data()) {
      await this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('collections')
        .doc(collectionId)
        .update({
          public: isPublic,
          dateAdded: new Date(),
        });

      await this.firebase.firestore().collection('collectionPublic').add({ uid, collectionId });
    }
  }

  async removeCardFromCollection(uid, collectionId, id) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('collections')
      .doc(collectionId)
      .collection('collection_cards')
      .doc(id)
      .delete();
  }

  exportCsv(arrayHeader, arrayData, fileName) {
    const processRow = function(row) {
      let finalVal = '';
      for (let j = 0; j < row.length; j += 1) {
        let innerValue = '';

        if (row[j] instanceof Date) {
          innerValue = row[j].toLocaleString();
        } else if (row[j] != null) {
          innerValue = row[j].toString();
        }

        let result = innerValue.replace(/"/g, '""');

        if (result.search(/("|,|\n)/g) >= 0) { result = `"${result}"`; }
        if (j > 0) { finalVal += ','; }

        finalVal += result;
      }
      return `${finalVal}\n`;
    };

    const header = processRow(arrayHeader);
    let csv = header;

    arrayData.forEach((array) => {
      csv += processRow(array);
    });

    const csvData = new Blob([csv], { type: 'text/csv; charset=utf-8' });
    const csvUrl = URL.createObjectURL(csvData);

    const hiddenElement = document.createElement('a');
    hiddenElement.href = csvUrl;
    hiddenElement.download = `${fileName}.csv`;
    hiddenElement.rel = 'noopener';
    hiddenElement.style.display = 'none';
    document.body.appendChild(hiddenElement);
    hiddenElement.click();
    document.body.removeChild(hiddenElement);
    setTimeout(() => URL.revokeObjectURL(csvUrl), 1000);
  }

  async exportCollection(uid, collectionId) {
      const activeHeaders = ['Date Purchased', 'Quantity', 'Card', 'Subject', 'Year', 'Set', 'Variation', 'Number', 'Category', 'Condition', 'Investment', 'Current Value', 'Potential Profit', 'Graded Cert #', 'Population', 'Notes'];
      const soldHeaders = ['Date Purchased', 'Quantity', 'Card', 'Subject', 'Year', 'Set', 'Variation', 'Number', 'Category', 'Condition', 'Investment', 'Date Sold', 'Sold Price', 'Profit', 'Graded Cert #', 'Population', 'Notes'];

      const activeData = [];
      const soldData = [];

      const collectionCardDocs = await this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('collections')
        .doc(collectionId)
        .collection('collection_cards')
        .orderBy('dateAdded')
        .get();

      collectionCardDocs.forEach((collectionCardDoc) => {
        if (snapshotExists(collectionCardDoc) && collectionCardDoc.data()) {
          const data = collectionCardDoc.data();

          const datePurchased = data.datePurchased && typeof data.datePurchased.toDate === 'function' ? moment(data.datePurchased.toDate()).format('MM/DD/yyyy') : '';
          const dateSold = data.dateSold && typeof data.dateSold.toDate === 'function' ? moment(data.dateSold.toDate()).format('MM/DD/yyyy') : '';

          const quantity = data.quantity || 1;
          const investment = data.investment || 0;
          const label = data.label || '';
          const player = data.player || '';
          const year = data.year || '';
          const set = data.set || '';
          const variation = data.variation || '';
          const number = data.number || '';
          const category = data.category || '';
          const grader = data.grader || '';
          const grade = data.grade || '';
          const autographGrade = data.autographGrade || '';
          const qualifierType = data.qualifierType || '';
          const condition = getConditionDisplay(grader, grade, autographGrade, qualifierType);
          const totalInvestment = (quantity * investment).toFixed(2);
          const currentValue = data.currentValue || 0;
          const cert = data.cert || '';
          const population = data.pop || '';
          const notes = data.notes || '';

          if (data.willManageQuantity && data.individualCards && data.individualCards.length > 0) {
            data.individualCards.forEach((item) => {
              const itemInvestment = item.investment || 0;
              const itemDatePurchased = item.datePurchased && typeof item.datePurchased.toDate === 'function'
                ? moment(item.datePurchased.toDate()).format('MM/DD/yyyy')
                : '';

              if (item.sold) {
                const itemPriceSold = item.priceSold || 0;
                soldData.push([
                  itemDatePurchased,
                  1,
                  label,
                  player,
                  year,
                  set,
                  variation,
                  number,
                  category,
                  condition,
                  itemInvestment,
                  item.dateSold && typeof item.dateSold.toDate === 'function' ? moment(item.dateSold.toDate()).format('MM/DD/yyyy') : '',
                  itemPriceSold,
                  itemPriceSold - itemInvestment,
                  cert,
                  population,
                  notes,
                ]);
              } else {
                activeData.push([
                  itemDatePurchased,
                  1,
                  label,
                  player,
                  year,
                  set,
                  variation,
                  number,
                  category,
                  condition,
                  itemInvestment,
                  currentValue.toFixed(2),
                  (currentValue - itemInvestment).toFixed(2),
                  cert,
                  population,
                  notes,
                ]);
              }
            });
          } else if (data.sold) {
            const priceSold = data.priceSold || 0;
            const totalPriceSold = (priceSold * quantity).toFixed(2);
            const totalProfit = (totalPriceSold - totalInvestment).toFixed(2);

            soldData.push([
              datePurchased,
              quantity,
              label,
              player,
              year,
              set,
              variation,
              number,
              category,
              condition,
              investment,
              dateSold,
              totalPriceSold,
              totalProfit,
              cert,
              population,
              notes,
            ]);
          } else {
            const totalValue = (currentValue * quantity).toFixed(2);
            const totalProfit = (totalValue - totalInvestment).toFixed(2);

            activeData.push([
              datePurchased,
              quantity,
              label,
              player,
              year,
              set,
              variation,
              number,
              category,
              condition,
              investment,
              totalValue,
              totalProfit,
              cert,
              population,
              notes,
            ]);
          }
        }
      });

      const downloads = [];

      if (activeData.length) {
        downloads.push(() => this.exportCsv(activeHeaders, activeData, 'Collection - Card Ladder'));
      }

      if (soldData.length) {
        downloads.push(() => this.exportCsv(soldHeaders, soldData, 'Sold - Card Ladder'));
      }

      downloads.forEach((download, index) => {
        if (index === 0) {
          download();
        } else {
          setTimeout(download, index * 250);
        }
      });

      return true;
  }

  async getWatchlistCard(uid, id) {
    const watchlistCardDoc = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('watchlist')
      .doc(id)
      .get();

    if (!watchlistCardDoc || !snapshotExists(watchlistCardDoc)) {
      return null;
    }

    const watchlistCard = {
      ...watchlistCardDoc.data(),
      id: watchlistCardDoc.id,
    };

    return watchlistCard;
  }

  async getWatchlistCardByCardId(uid, cardId) {
    if (!cardId) {
      return null;
    }

    const watchlistCardQuery = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('watchlist')
      .where('cardId', '==', cardId)
      .get();

    let watchlistCard = {};

    watchlistCardQuery.forEach((watchlistCardDoc) => {
      if (snapshotExists(watchlistCardDoc) && watchlistCardDoc.data()) {
        watchlistCard = {
          ...watchlistCardDoc.data(),
          id: watchlistCardDoc.id,
          watchlistCardId: watchlistCardDoc.id,
        };
      }
    });

    return watchlistCard;
  }

  async addToWatchlist(uid, card) {
    const cardToAdd = clone(card);

    cardToAdd.dateAdded = new Date();

    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('watchlist')
      .doc(card.cardId)
      .set(cardToAdd);
  }

  async removeFromWatchlist(uid, cardId) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('watchlist')
      .doc(cardId)
      .delete();
  }

  async updateWatchlistCard(uid, cardId, update) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('watchlist')
      .doc(cardId)
      .update(update);
  }

  async createOrUpdateComparison(uid, comparison) {
    const savedComparison = clone(comparison);

    delete savedComparison.active;
    delete savedComparison.description;
    delete savedComparison.id;

    if (comparison.id) {
      return this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('comparisons')
        .doc(comparison.id)
        .update(savedComparison);
    } else {
      return this.firebase.firestore()
        .collection('users')
        .doc(uid)
        .collection('comparisons')
        .add(savedComparison);
    }
  }

  async removeComparisons(uid, id) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('comparisons')
      .doc(id)
      .delete();
  }

  async getEmployeeOptions() {
    const adminDocs = await this.firebase.firestore()
      .collection('users')
      .where('role', '==', 'admin')
      .get();

    const staffDocs = await this.firebase.firestore()
      .collection('users')
      .where('role', '==', 'staff')
      .get();

    const employeeOptions = [];

    adminDocs.forEach((doc) => {
      if (doc.data() && doc.data().displayName) {
        employeeOptions.push({
          value: doc.id,
          label: doc.data().displayName,
          role: doc.data().role,
        });
      }
    });

    staffDocs.forEach((doc) => {
      if (doc.data() && doc.data().displayName) {
        employeeOptions.push({
          value: doc.id,
          label: doc.data().displayName,
          role: doc.data().role,
        });
      }
    });

    return employeeOptions;
  }

  async getCurrencyMap() {
    try {
      const currencyMapDoc = await this.firebase.firestore()
        .collection('config')
        .doc('currencyMap')
        .get();

      return currencyMapDoc.data();
    } catch (e) {
      return { 'en-us': { ratio: 1, symbol: '$' } };
    }
  }

  async getCardStatsLastSync() {
    const doc = await this.firebase.firestore()
      .collection('config')
      .doc('cardStats')
      .get();

    const lastSync = doc.data().lastSync.toDate();

    return lastSync;
  }

  watchSavedFilters({ uid, type, onSnapshot }) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('savedFilters')
      .where('type', '==', type)
      .onSnapshot((querySnapshot) => {
        if (!querySnapshot) return;

        const savedFilters = [];

        querySnapshot.forEach((doc) => {
          const filter = doc.data();

          if (filter.filters && filter.filters.minDate && typeof filter.filters.minDate.toDate === 'function') {
            filter.filters.minDate = filter.filters.minDate.toDate();
          }

          if (filter.filters && filter.filters.maxDate && typeof filter.filters.maxDate.toDate === 'function') {
            filter.filters.maxDate = filter.filters.maxDate.toDate();
          }

          let description = filter.query || '';
          const filterDescription = getFilterDescription(filter.filters);

          if (description && filterDescription) {
            description += ', ';
          }

          if (filterDescription) {
            description += filterDescription;
          }

          savedFilters.push({
            id: doc.id,
            description,
            ...filter,
          });
        });

        onSnapshot(savedFilters);
      });
  }

  async getSavedFilters(uid, type) {
    const savedFilters = [];
    const savedFilterDocs = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('savedFilters')
      .where('type', '==', type)
      .get();

    savedFilterDocs.forEach((savedFilterDoc) => {
      savedFilters.push({
        ...savedFilterDoc.data(),
        id: savedFilterDoc.id,
      });
    });

    return savedFilters;
  }

  async addToSavedFilters(uid, filter) {
    const savedFilter = clone(filter);

    delete savedFilter.active;
    delete savedFilter.description;

    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('savedFilters')
      .add(savedFilter);
  }

  async updateSavedFilter(uid, filter) {
    const savedFilter = clone(filter);

    delete savedFilter.id;
    delete savedFilter.active;
    delete savedFilter.description;

    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('savedFilters')
      .doc(filter.id)
      .set(savedFilter);
  }

  async removeFromSavedFilters(uid, filterId) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('savedFilters')
      .doc(filterId)
      .delete();
  }

  async likeShowcaseCard(uid, collectionCard, like = true) {
    return this.firebase.firestore().collection('showcaseLikes').add({
      uid: collectionCard.uid,
      collectionId: collectionCard.collectionId,
      collectionCardId: collectionCard.collectionCardId,
      likeUid: uid,
      like,
    });
  }

  async getCustomIndexes(uid) {
    const indexDocs = await this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('customIndexes')
      .get();

    const indexes = [];

    indexDocs.forEach((indexDoc) => {
      indexes.push({
        ...indexDoc.data(),
        id: indexDoc.id,
      });
    });

    return indexes;
  }

  async deleteCustomIndex(uid, id) {
    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('customIndexes')
      .doc(id)
      .delete();
  }

  async createCustomIndex(uid, index) {
    const indexToCreate = clone(index);

    if (index.type === 'subset' && index.filters) {
      const clauses = [];

      if (index.filters.minValue) {
        clauses.push({ left: 'currentValue', middle: '>=', right: index.filters.minValue });
      }

      if (index.filters.maxValue) {
        clauses.push({ left: 'currentValue', middle: '<=', right: index.filters.maxValue });
      }

      if (index.filters.minYear) {
        clauses.push({ left: 'year', middle: '>=', right: index.filters.minYear });
      }

      if (index.filters.maxYear) {
        clauses.push({ left: 'year', middle: '<=', right: index.filters.maxYear });
      }

      if (index.filters.minPop) {
        clauses.push({ left: 'pop', middle: '>=', right: index.filters.minPop });
      }

      if (index.filters.maxPop) {
        clauses.push({ left: 'pop', middle: '<=', right: index.filters.maxPop });
      }

      indexToCreate.filters.categories = [];
      indexToCreate.filters.conditions = [];
      delete indexToCreate.filters.category;

      if (index.filters.category) {
        indexToCreate.filters.categories.push(index.filters.category);
        clauses.push({ left: 'category', middle: '==', right: index.filters.category });
      }

      if (index.filters.conditions && index.filters.conditions.length) {
        const conditions = index.filters.conditions.map(({ value }) => value);

        indexToCreate.filters.conditions = conditions;
        clauses.push({ left: 'condition', middle: 'in', right: conditions });
      }

      if (index.filters.player) {
        clauses.push({ left: 'player', middle: '==', right: index.filters.player });
      }

      indexToCreate.clauses = clauses;
    }

    return this.firebase.firestore()
      .collection('users')
      .doc(uid)
      .collection('customIndexes')
      .add(indexToCreate);
  }
}
